import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  User,
  Target,
  Briefcase,
  Rocket,
  Megaphone,
  Calculator,
  FolderPlus,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';

export interface ArtifactReference {
  type: string;
  recordId: string;
  status: 'completed' | 'failed';
  summary?: string;
}

const ARTIFACT_CONFIG: Record<string, {
  icon: typeof BarChart3;
  labelKey: string;
  path: string;
  color: string;
}> = {
  market_analysis: {
    icon: BarChart3,
    labelKey: 'artifact.labels.market_analysis',
    path: '/market-analysis',
    color: 'text-blue-500',
  },
  persona: {
    icon: User,
    labelKey: 'artifact.labels.persona',
    path: '/personas',
    color: 'text-purple-500',
  },
  positioning: {
    icon: Target,
    labelKey: 'artifact.labels.positioning',
    path: '/positioning',
    color: 'text-orange-500',
  },
  business_model: {
    icon: Briefcase,
    labelKey: 'artifact.labels.business_model',
    path: '/business-model',
    color: 'text-green-500',
  },
  mvp_plan: {
    icon: Rocket,
    labelKey: 'artifact.labels.mvp_plan',
    path: '/mvp-plan',
    color: 'text-red-500',
  },
  marketing_strategy: {
    icon: Megaphone,
    labelKey: 'artifact.labels.marketing_strategy',
    path: '/marketing-strategy',
    color: 'text-pink-500',
  },
  financial_model: {
    icon: Calculator,
    labelKey: 'artifact.labels.financial_model',
    path: '/financial-model',
    color: 'text-emerald-500',
  },
  project: {
    icon: FolderPlus,
    labelKey: 'artifact.labels.project',
    path: '/projects',
    color: 'text-indigo-500',
  },
};

interface ArtifactCardProps {
  artifact: ArtifactReference;
  projectId?: string;
}

export default function ArtifactCard({ artifact, projectId: _projectId }: ArtifactCardProps) {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { localizedPath } = useLanguage();
  const config = ARTIFACT_CONFIG[artifact.type];

  if (!config) return null;

  const Icon = config.icon;
  const isCompleted = artifact.status === 'completed';
  const basePath = artifact.recordId
    ? `${config.path}?recordId=${artifact.recordId}`
    : config.path;
  const targetUrl = localizedPath(basePath);

  return (
    <div
      className="flex items-center gap-3 mt-2 p-3 rounded-[12px] border border-[var(--grey-accent,#eeeeee)] bg-white cursor-pointer hover:border-[var(--main-brand,#2197f1)] transition-colors"
      onClick={() => navigate(targetUrl)}
    >
      <div className={`flex-shrink-0 ${config.color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-manrope font-semibold text-[13px] leading-[18px] text-[color:var(--black-text,#0d0d0d)]">
            {t(config.labelKey)}
          </span>
          {isCompleted ? (
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
          )}
        </div>
        {artifact.summary && (
          <p className="font-segoe text-[12px] leading-[16px] text-[color:var(--grey-text,#565858)] line-clamp-2">
            {artifact.summary}
          </p>
        )}
      </div>
      <button
        className="flex-shrink-0 px-[12px] py-[4px] rounded-[8px] bg-[var(--main-brand,#2197f1)] text-white font-manrope font-semibold text-[12px] leading-[16px] hover:opacity-90 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          navigate(targetUrl);
        }}
      >
        {t('artifact.buttonOpen')}
      </button>
    </div>
  );
}
